import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';
import { ArrowLeftOutlined } from '@ant-design/icons';
import WizrFlexLayout from '../../../../../components/WizrFlexLayout';
import WizrUserJourney from '../../../../../components/WizrUserJourney';
import WizrButton from '../../../../../components/WizrButton';
import WizrText from '../../../../../components/WizrText';
import WizrTag from '../../../../../components/WizrTags';
import WizrDragDropTable from '../../../../../components/WizrDragDropTable';
import { useBotDetailsSelector, useEvalHistorySelector } from '../../../../../redux/selectors/content';
import { dispatchBotDetails, dispatchGetEvalHistory } from '../../../../../redux/actions/content';
import WizrLoader from '../../../../../components/WizrLoader';
import NewTabIcon from '../../../../../assets/icons/NewTabIcon';
import { formatEvalHistoryStartTimeLocal } from '../../../../../utils/evalHistoryStartTime';

// Constants
const STATUS_COMPLETED = 'COMPLETED';
const STATUS_IN_PROGRESS = 'IN_PROGRESS';
const STATUS_COLORS = {
  COMPLETED: '#22C55E',
  ERROR: '#EF4444',
  IN_PROGRESS: '#F59E0B',
} as const;

const ICON_COLORS = {
  DISABLED: '#B8BBBF',
  ENABLED: '#0000FF',
} as const;

const StyledViewLink = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${props => props.disabled ? ICON_COLORS.DISABLED : ICON_COLORS.ENABLED};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  
  &:hover {
    color: ${props => props.disabled ? ICON_COLORS.DISABLED : ICON_COLORS.ENABLED};
  }
`;

// Styled components for loading and empty states
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  color: #707276;
`;

const ContentContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  background: #FFFFFF;
  border-radius: 8px;
`;

const TableCardWrapper = styled.div`
  background: #FFFFFF;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  .wizr-drag-drop-table {
    .ant-table {
      background: #FFFFFF;
      border: none;
    }
    
    .ant-table-container {
      border: none;
    }
    
    .ant-table-thead > tr > th {
      background: #F5F5F5 !important;
      color: #1D1F22 !important;
      font-weight: 600 !important;
      font-size: 14px;
      text-align: center !important;
      padding: 12px 16px;
      border-right: 1px solid #E5E7EB;
      border-bottom: 1px solid #E5E7EB;
      
      &:last-child {
        border-right: none;
      }
      
      .ant-table-column-sorters {
        justify-content: center;
      }
      
      .ant-table-column-title {
        text-align: center;
      }
    }
    
    .ant-table-tbody > tr > td {
      background: #FFFFFF;
      color: #1D1F22;
      font-size: 14px;
      text-align: left;
      padding: 12px 16px;
      border-right: 1px solid #E5E7EB;
      border-bottom: 1px solid #E5E7EB;
      
      &:last-child {
        border-right: none;
      }
    }
    
    .ant-table-tbody > tr:last-child > td {
      border-bottom: none;
    }
    
    .ant-table-tbody > tr:hover > td {
      background: #F3F4F6;
    }
    
    .ant-table-column-sorter {
      color: #999B9F;
    }
    
    .ant-table-column-sorter-up.active,
    .ant-table-column-sorter-down.active {
      color: #1890ff;
    }
  }
`;

// Format duration from minutes to "X min" or "X hr Y min" string format
const formatDuration = (minutes: number | string | null | undefined): string => {
  if (minutes === null || minutes === undefined || minutes === 'N/A') {
    return 'N/A';
  }
  
  // If it's already a string, return it (might be "N/A" or already formatted)
  if (typeof minutes === 'string') {
    return minutes;
  }
  
  // Convert number to formatted string
  if (typeof minutes === 'number') {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours} hr ${mins} min` : `${hours} hr`;
  }
  
  return 'N/A';
};

const AutoSolveEvalsHistory = () => {
  // Redux selectors
  const botDetails = useBotDetailsSelector();
  const evalHistoryFromApi = useEvalHistorySelector();
  const [isLoading, setIsLoading] = useState(false);
  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isFetchingRef = useRef(false);
  
  // Track if botDetails has been fetched to prevent duplicate calls
  const botDetailsFetchedRef = useRef<string | undefined>(undefined);

  // Get agent_id directly from URL params
  const { id: agent_id } = useParams();

  // Transform API data to match component structure
  const evalsHistoryData = React.useMemo(() => {
    if (!evalHistoryFromApi || !Array.isArray(evalHistoryFromApi)) {
      return [];
    }
    return evalHistoryFromApi.map((item: any, index: number) => {
      const transformedId = item.id ?? item.eval_id ?? item.run_id ?? index + 1;
 
      // Normalize status to uppercase
      const status = item.status ? item.status.toUpperCase() : STATUS_COMPLETED;
      
      const { display: startTime, ms: startTimeMs } = formatEvalHistoryStartTimeLocal(
        item.start_time_iso,
        item.start_time
      );
      return {
        id: transformedId,
        datasetName: item.dataset_name ?? item.datasetName ?? `Dataset ${index + 1}`,
        duration: formatDuration(item.average_latency_minutes),
        startTime,
        startTimeMs,
        status: status,
        langfusePath: item.langfuse_path ?? null
      };
    });
  }, [evalHistoryFromApi]);

  const navigate = useNavigate();

  // Fetch bot details once for agent name in breadcrumb
  useEffect(() => {
    if (agent_id && agent_id !== botDetailsFetchedRef.current) {
      botDetailsFetchedRef.current = agent_id;
      dispatchBotDetails(agent_id);
    }
  }, [agent_id]);

  useEffect(() => {
    // Fetch eval history when agent_id is available
    // Since agent_id comes from URL params, it only changes on route change,
    // so we don't need duplicate prevention logic
    if (agent_id) {
      setIsLoading(true);
      dispatchGetEvalHistory('agent_id', agent_id)
        .finally(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [agent_id]);

  // Lightweight fetch function used by polling to avoid spinner flicker
  const refetchHistorySilently = React.useCallback(() => {
    if (!agent_id) return;
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    dispatchGetEvalHistory('agent_id', agent_id)
      .finally(() => {
        isFetchingRef.current = false;
      })
      .catch(() => {
        isFetchingRef.current = false;
      });
  }, [agent_id]);

  // Start/stop polling while any run is not completed
  useEffect(() => {
    if (!agent_id) return;

    const hasPending =
      Array.isArray(evalsHistoryData) &&
      evalsHistoryData.some((row: any) => (row?.status ?? '').toUpperCase() !== STATUS_COMPLETED);

    const shouldPoll = hasPending && document.visibilityState === 'visible';

    if (shouldPoll && !pollingTimerRef.current) {
      // Initial immediate refetch to reduce perceived latency
      refetchHistorySilently();
      pollingTimerRef.current = setInterval(() => {
        refetchHistorySilently();
      }, 5000);
    } else if (!shouldPoll && pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }

    return () => {
      // Cleanup when dependencies change
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [agent_id, evalsHistoryData, refetchHistorySilently]);

  // Pause/resume polling based on tab visibility and trigger a refresh when visible again
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refetchHistorySilently();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refetchHistorySilently]);

  const handleViewReport = React.useCallback((item: { id: string | number; status: string; langfusePath: string | null }) => {
    if (item.status === STATUS_COMPLETED && item.langfusePath) {
      window.open(item.langfusePath, '_blank', 'noopener,noreferrer');
    }
  }, []);

  // Memoize mappedData to prevent unnecessary recalculations
  const mappedData = useMemo(() => {
    if (!evalsHistoryData || evalsHistoryData.length === 0) {
      return [];
    }

    return evalsHistoryData.map((item) => {
      const normalizedStatus = (item.status ?? '').toUpperCase();
      const isCompleted = normalizedStatus === STATUS_COMPLETED;
      const isInProgress = normalizedStatus === STATUS_IN_PROGRESS;
      const hasLangfusePath = !!item.langfusePath;
      const isLinkEnabled = isCompleted && hasLangfusePath;
      return {
        key: item.id,
        dataset_name: item.datasetName,
        duration: item.duration ?? 'N/A',
        start_time: item.startTime ?? 'N/A',
        start_time_ms: item.startTimeMs ?? undefined,
        status: (
          <WizrTag
            data={[normalizedStatus || STATUS_COMPLETED]}
            background={
              isCompleted
                ? STATUS_COLORS.COMPLETED
                : isInProgress
                ? STATUS_COLORS.IN_PROGRESS
                : STATUS_COLORS.ERROR
            }
            textColor="#FFFFFF"
            checkable={false}
            style={{
              fontWeight: 500,
              padding: '4px 12px',
              borderRadius: '4px',
            }}
          />
        ),
        report: (
          <StyledViewLink
            disabled={!isLinkEnabled}
            onClick={() => handleViewReport(item)}
          >
            <span>View</span>
            <NewTabIcon 
              fill={!isLinkEnabled ? ICON_COLORS.DISABLED : ICON_COLORS.ENABLED} 
              width={24} 
              height={24} 
            />
          </StyledViewLink>
        ),
      };
    });
  }, [evalsHistoryData, handleViewReport]);

  return (
    <WizrFlexLayout>
      <WizrFlexLayout flex='none' justifyContent='space-between' alignItems='center' flexDirection='row'>
        <WizrUserJourney
          userJourney={[
            {
              title: (
                <WizrButton
                  icon={<ArrowLeftOutlined style={{ fontSize: "22px", marginRight: "10px" }} />}
                  type='text'
                  text={<WizrText type='h6'>{botDetails?.agent_config?.agent_name ?? 'Agent'}</WizrText>}
                  onClick={() => navigate(-1)}
                />
              ),
            },
            { title: 'Evals' },
            { title: 'History' },
          ]}
        />
      </WizrFlexLayout>
      <ContentContainer>
        {isLoading ? (
          <LoadingContainer>
            <WizrLoader
              loading={isLoading}
              size="default"
            />
          </LoadingContainer>
        ) : mappedData.length > 0 ? (
          <TableCardWrapper>
            <WizrDragDropTable
              type="autosolveevalshistory"
              dataSource={mappedData}
              rowKey="key"
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </TableCardWrapper>
        ) : (
          <EmptyStateContainer>
            <WizrText type="body2" textColor="#707276">No eval history available</WizrText>
          </EmptyStateContainer>
        )}
      </ContentContainer>
    </WizrFlexLayout>
  );
};

export default AutoSolveEvalsHistory;